import { useRef, useState } from 'react'
import { Button, Drawer } from 'antd'
import { MenuOutlined } from '@ant-design/icons'
import { useParams, useSearchParams } from 'react-router-dom'
import { useDevice } from '../../hooks/useDevices'
import type { Device } from '../../types/device.types'
import DeviceActions from '../../components/devices/DeviceActions'
import type { DeviceActionsRef } from '../../components/devices/DeviceActions'
import { DeviceDetailSidebar, MapAndActionsView, ActivityLogView, CommandLogView, HardwareInfoView } from '../../components/devices/detail'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { QueryStateView } from '../../components/common/QueryStateView'

const VALID_SECTIONS = ['map-actions', 'activity-log', 'command-log', 'hardware-info'] as const
type Section = (typeof VALID_SECTIONS)[number]

export default function DeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const deviceQuery = useDevice(deviceId!)
  const actionsRef = useRef<DeviceActionsRef>(null)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [drawerOpen, setDrawerOpen] = useState(false)

  const rawSection = searchParams.get('section') || 'map-actions'
  const section: Section = VALID_SECTIONS.includes(rawSection as Section)
    ? (rawSection as Section)
    : 'map-actions'

  const handleSectionChange = (newSection: string) => {
    setSearchParams({ section: newSection }, { replace: true })
    if (isMobile) setDrawerOpen(false)
  }

  return (
    <QueryStateView query={deviceQuery}>
      {(device) => <DeviceDetailContent
        device={device}
        section={section}
        actionsRef={actionsRef}
        isMobile={isMobile}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        handleSectionChange={handleSectionChange}
      />}
    </QueryStateView>
  )
}

function DeviceDetailContent({
  device,
  section,
  actionsRef,
  isMobile,
  drawerOpen,
  setDrawerOpen,
  handleSectionChange,
}: {
  device: Device
  section: Section
  actionsRef: React.RefObject<DeviceActionsRef | null>
  isMobile: boolean
  drawerOpen: boolean
  setDrawerOpen: (open: boolean) => void
  handleSectionChange: (section: string) => void
}) {
  const sidebarContent = (
    <DeviceDetailSidebar
      device={device}
      activeSection={section}
      onSectionChange={handleSectionChange}
      onDelete={() => actionsRef.current?.openDestroy()}
    />
  )

  const contentView = (() => {
    switch (section) {
      case 'activity-log':
        return <ActivityLogView deviceId={device.device_id} />
      case 'command-log':
        return <CommandLogView deviceId={device.device_id} />
      case 'hardware-info':
        return <HardwareInfoView pingInfo={device.pingInfo} />
      case 'map-actions':
      default:
        return <MapAndActionsView device={device} actionsRef={actionsRef} />
    }
  })()

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        margin: -32,
        width: 'calc(100% + 64px)',
        overflow: 'hidden',
      }}
    >
      {/* Desktop sidebar */}
      {!isMobile && sidebarContent}

      {/* Mobile drawer */}
      {isMobile && (
        <>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 20,
              background: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              width: 40,
              height: 40,
            }}
          />
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            placement="left"
            styles={{ body: { padding: 0 }, wrapper: { width: 320 } }}
            closable={false}
          >
            {sidebarContent}
          </Drawer>
        </>
      )}

      {/* Main content */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {contentView}
      </div>

      {/* Headless DeviceActions -- renders only modals */}
      <DeviceActions
        ref={actionsRef}
        deviceId={device.device_id}
        isOnline={device.isOnline}
        headless
      />
    </div>
  )
}
